import {
    Avatar,
    Badge,
    Button,
    Card,
    Divider, Empty, Modal
} from "antd";
import {  useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { axios } from "../../../../axiosConfig";
import { selectUser } from "../../../../features/user/userSlice";
import CreatePositionForm from "./forms/createPosition.form";
import { useTranslation } from "react-i18next";
import InteractionsSection from "./sections/interactions.section";
import MergedContactsSection from "./sections/mergedContacts/mergedContacts.section";
import { NotesSection } from "./sections/notes/notes.section";
import { ContactHooks } from "./hooks";
import { CardBreadCrumb, ContactInfoDescriptions, EditPositionButton, GeneratePositionsList, UpdateContactForm } from "./components";
import './contact.css';

const ContactView = ({ contact, shouldReload, setShouldReload }) => {
    const { t } = useTranslation();
    const user = useSelector(selectUser);
    const isStaff = user?.role === 'validated' || user?.role === 'admin';

    const [currentContact, setCurrentContact] = useState(contact);
    const [notes, setNotes] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);
    const [autoSaveNotes, setAutoSaveNotes] = useState(true);
    const [saveTimeoutId, setSaveTimeoutId] = useState(0);

    const [editingContact, setEditingContact] = useState(false);
    const [saveConctactLoading, setSaveContactLoading] = useState(false);
    const [savePositionLoading, setSavePositionLoading] = useState(false);
    const [showCreatePositionModal, setShowCreatePositionModal] = useState(false);
    const [showUpdatePositionModal, setShowUpdatePositionModal] = useState(false);
    const [editingPosition, setEditingPosition] = useState(false);
    const [deletingContact, setDeletingContact] = useState(false);
    const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
    const [createPositionParam, setCreatePositionParam] = useState(null);

    const formRef = useRef(null);
    const navigate = useNavigate();
    
    const [includesEndsCalendar, setIncludesEndsCalendar] = useState(false);


    // Inline useQuery
    const query = new URLSearchParams(useLocation().search);
    const createPosition = query.get('createPosition');

    useEffect(() => {
        if (createPosition !== createPositionParam) {
            setCreatePositionParam(createPosition);
            setShowCreatePositionModal(!!createPosition);
        }
    }, [createPosition]);

    // Sincronizar el contacto local cuando cambie el prop
    useEffect(() => {
        setCurrentContact(contact);
    }, [contact]);

    // Función para actualizar el contacto después de un merge
    const handleContactUpdated = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/${contact.id}`);
            setCurrentContact(response.data.data);
        } catch (error) {
            console.error('Error actualizando contacto:', error);
            // Como fallback, activar el reload general
            setShouldReload(shouldReload + 1);
        }
    };


    const handleSubmitContact = () => {
        formRef.current.submit();
    };

    const dateOptions = {
        day: "numeric",
        month: "numeric",
        year: "numeric"
    };

    const deletePosition = (position) => {
        axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/positions/${position.id}`)
            .then(() => setShouldReload(shouldReload + 1))
    };

    const deleteContact = () => {
        const hasPositions = currentContact.positions && currentContact.positions.length > 0;

        if (hasPositions) {
            Modal.warning({
                title: t("contacts.modalDeleteContact.title"),
                content: t("contacts.modalDeleteContact.content"),
                onOk: () => {
                    setShowDeleteContactModal(false)
                }
            }); 
        } else {
            setDeletingContact(true)
            try {
                axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/${currentContact.id}`)
                    .then(() => navigate('/contacts'))
                    .finally(() => setDeletingContact(false))
            } catch (error) {
                console.error(error)
            };
        }
    };

    const onFinishEditing = (values) => {
        // Update the contact object with the form values
        const updatedContact = { ...currentContact };
        updatedContact.name = values.name;
        updatedContact.surname = values.surname;
        updatedContact.address = values.address;
        updatedContact.zipCode = values.zipCode;
        updatedContact.phone = values.phone;
        updatedContact.email = values.email;

        // Save the updated contact object
        updateContact(updatedContact);
    };

    const updateContact = (updatedContact) => {
        setSaveContactLoading(true);
        axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/${currentContact.id}`, updatedContact)
            .then((response) => {
                // Actualizar el contacto local con los datos del servidor
                setCurrentContact(response.data.data || updatedContact);
            })
            .catch()
            .finally(() => {
                setSaveContactLoading(false);
                setEditingContact(false);
                setShouldReload(shouldReload + 1);
            });
    };

    const lastUpdatedDifference = (pastDate) => {
        // Get the current time in milliseconds
        const currentTime = new Date().getTime();


        // Get the number of milliseconds since the Unix epoch for the past date
        const pastTime = pastDate.getTime();

        // Calculate the difference in milliseconds
        const timeDifference = currentTime - pastTime;

        // Convert the difference in milliseconds to days by dividing by the number of milliseconds in a day
        return Math.round(timeDifference / (1000 * 60 * 60 * 24));
    }


    // Function to handle updating the notes in the ReactQuill
    const handleNotesChange = (value) => {
        setNotes(value);
        if (saveTimeoutId) {
            clearTimeout(saveTimeoutId);
        }
        if (autoSaveNotes) setSaveTimeoutId(setTimeout(() => {
            handleNotesSubmit(value)
        }, 1000))
    }

    // Function to handle submitting the notes to the specified endpoint
    const handleNotesSubmit = (newNotes) => {
        // Make a request to the specified endpoint using axios
        setSavingNotes(true);
        axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/v1/contacts/${currentContact.id}/notes`, { notes: newNotes })
            .then(response => {
                // Do something with the response
            })
            .catch(error => {
                // Do something with the error
            })
            .finally(() => setTimeout(() =>
                setSavingNotes(false)
                , 500));
    };


    //
    const [showModal, setShowModal] = useState({
        createInteraction: false,
        updateInteraction: false
    });

    const {editContactButton, showFromInteraction} = ContactHooks({
                                             editingContact,
                                             setEditingContact,
                                             setShowDeleteContactModal, 
                                             saveConctactLoading, 
                                             handleSubmitContact,
                                             showModal, 
                                             setShowModal});


 useEffect(() => {
     currentContact && handleNotesChange(currentContact.notes);
   }, [currentContact]);


    return <>
       <CardBreadCrumb contact={currentContact} />
        <div style={{ padding: '2em' }}>
            <Badge.Ribbon text={<>{t("contacts.flag.update")} {lastUpdatedDifference(new Date(currentContact.updatedAt))} {t("contacts.flag.days")}</>}>
                <Card title={<div style={{ display: 'flex', gap: '1em', alignItems: "center" }}>{currentContact.picture && <Avatar size="large" src={currentContact.picture} className="header-menu-avatar" />}
                    {currentContact.name} {currentContact.surname ?? ''}</div>}>
                    {editingContact ? 
                    <UpdateContactForm
                        contact={currentContact}
                        editContactButton={editContactButton}
                        formRef={formRef}
                        onFinishEditing={onFinishEditing}
                       /> 
                    : 
                    <ContactInfoDescriptions 
                    contact={currentContact}
                    editContactButton={editContactButton}
                    />
                    }
                    <Divider>{t("contacts.positions")}</Divider>
                    {currentContact?.positions?.length > 0 &&   <EditPositionButton 
                                                       editingPosition={editingPosition}
                                                       setEditingPosition={setEditingPosition}
                                                       savePositionLoading={savePositionLoading}
                                                       saveConctactLoading={saveConctactLoading}
                                                       setIncludesEndsCalendar={setIncludesEndsCalendar}
                                                       setShowCreatePositionModal={setShowCreatePositionModal}
                                                       /> }
                    {currentContact?.positions?.length > 0 ? 
                                                  <GeneratePositionsList
                                                contact={currentContact}
                                                deletePosition={deletePosition}
                                                editingPosition={editingPosition}
                                                savePositionLoading={savePositionLoading}
                                                setShowUpdatePositionModal={setShowUpdatePositionModal}
                                                />
                                                 : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("contacts.no_positions")}>
                        <Button type="default" onClick={() => setShowCreatePositionModal(true)}>{t("contacts.add_positions")}</Button>
                    </Empty>}
                    <NotesSection notes={notes} handleNotesChange={handleNotesChange} />
                    {showFromInteraction()}
                    <InteractionsSection contact={currentContact} handleNotesChange={handleNotesChange} showModal={showModal} setShowModal={setShowModal} />
                    {isStaff && (
                        <MergedContactsSection contact={currentContact} onContactUpdated={handleContactUpdated} />
                    )}
                </Card>
            </Badge.Ribbon>
        </div>
        <Modal
            footer={null}
            open={showCreatePositionModal}
            onCancel={() => setShowCreatePositionModal(false)}>
            <CreatePositionForm onFinish={() => {
                setShowCreatePositionModal(false);
                setShouldReload(shouldReload + 1);
            }} contact={currentContact}
                includesEndsCalendar={includesEndsCalendar}
                setIncludesEndsCalendar={setIncludesEndsCalendar}
            />
        </Modal>
        <Modal
            footer={null}
            open={showUpdatePositionModal}
            onCancel={() => setShowUpdatePositionModal(false)}>
            <CreatePositionForm onFinish={() => {
                setShouldReload(shouldReload + 1);
                setShowUpdatePositionModal(false);
            }} contact={currentContact} initialValues={showUpdatePositionModal}
                includesEndsCalendar={includesEndsCalendar}
                setIncludesEndsCalendar={setIncludesEndsCalendar}
            />
        </Modal>
        <Modal
            title={t("contacts.delete_contact_modal_title")}
            open={showDeleteContactModal}
            onOk={deleteContact}
            confirmLoading={deletingContact}
            onCancel={() => setShowDeleteContactModal(false)}
        >
            {t("contacts.delete_contact_modal_description")}
        </Modal>
    </>
}

export default ContactView;
